/* ============================================================
   api-client.js — wrapper fetch unifié pour le dashboard React
   ------------------------------------------------------------
   Auto-injection du token JWT (storage compatible Capacitor + web),
   parsing JSON défensif, gestion uniformisée des erreurs 401/500.

   Usage :
     import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api-client';
     const data = await apiGet('/api/dashboard/google-reviews?sort=newest');
     await apiPost('/api/reviews/collect', { prenom, telephone });
   ============================================================ */
import { API_BASE } from '../config/api';
import { storage } from './platform';

/** Construit les headers avec le JWT + X-Restaurant-Id si dispo. */
async function buildHeaders(extra = {}) {
  const token = await storage.get('token');
  // Multi-resto : si l'utilisateur a switché de resto, envoyer son id.
  let restaurantId = null;
  try { restaurantId = localStorage.getItem('revyo_current_restaurant_id'); } catch {}
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(restaurantId ? { 'X-Restaurant-Id': restaurantId } : {}),
    ...extra,
  };
}

/**
 * Fetch wrapper :
 *  - prefix automatique avec API_BASE
 *  - injection JWT
 *  - parsing JSON défensif (Railway peut renvoyer du HTML sur 502)
 *  - 401 → flush token + redirect /login
 *  - autres erreurs HTTP → throw avec status + payload
 */
export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const headers = await buildHeaders(options.headers);

  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch (networkErr) {
    const err = new Error('Connexion impossible. Vérifiez votre internet.');
    err.code = 'NETWORK';
    throw err;
  }

  // 401 = session expirée → comportement adaptatif :
  //  - Si une impersonation est en cours (revyo_impersonation présent en
  //    storage), c'est très probablement le JWT impersonation qui a expiré
  //    (TTL 2h). On restaure le JWT owner depuis revyo_owner_token, on
  //    nettoie les clés d'impersonation, et on redirige /owner — l'owner
  //    n'est PAS éjecté vers /login.
  //  - Sinon (comportement nominal) : flush du token, redirect /login.
  if (res.status === 401) {
    const impersonationRaw = await storage.get('revyo_impersonation');
    if (impersonationRaw) {
      const ownerTok = await storage.get('revyo_owner_token');
      if (ownerTok) await storage.set('token', ownerTok);
      else await storage.remove('token');
      await storage.remove('revyo_owner_token');
      await storage.remove('revyo_impersonation');
      try { localStorage.removeItem('revyo_current_restaurant_id'); } catch {}
      if (typeof window !== 'undefined') {
        window.location.href = '/owner';
      }
      const err = new Error('Session pilotage expirée. Retour identité owner.');
      err.status = 401;
      err.code = 'IMPERSONATION_EXPIRED';
      throw err;
    }
    await storage.remove('token');
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    const err = new Error('Session expirée. Reconnectez-vous.');
    err.status = 401;
    throw err;
  }

  // 402 = compte en pause / essai expiré (middleware checkActiveAccount).
  // Au lieu de laisser chaque page afficher une erreur générique, on route
  // vers l'onglet Abonnement (routes /api/account + /api/stripe non gatées)
  // pour que l'utilisateur puisse choisir un plan et payer immédiatement.
  // Garde anti-boucle : pas de redirect si on est déjà sur /abonnement
  // (le wrapper DashboardTab y fetch aussi /api/dashboard → 402 attendu).
  if (res.status === 402) {
    // On lit le corps AVANT de rediriger pour distinguer les deux cas de 402 :
    //   - 'account_paused'              → compte en pause / essai expiré
    //   - 'onboarding_payment_required' → inscrit par la porte payante, checkout
    //                                     jamais finalisé (requireOnboardingPayment)
    // Le motif part en query param pour que TabAbonnement affiche le bon
    // bandeau au lieu d'atterrir sans explication.
    let payload402 = null;
    try { payload402 = await res.clone().json(); } catch { /* corps vide/HTML */ }
    const reason = payload402?.error === 'onboarding_payment_required'
      ? 'onboarding_payment_required'
      : 'account_paused';

    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/abonnement')) {
      window.location.href = `/abonnement?reason=${reason}`;
    }
    const err = new Error(reason === 'onboarding_payment_required'
      ? 'Paiement à finaliser pour accéder à votre tableau de bord.'
      : 'Compte en pause — abonnement requis pour continuer.');
    err.status = 402;
    err.code = reason === 'onboarding_payment_required' ? 'ONBOARDING_PAYMENT_REQUIRED' : 'ACCOUNT_PAUSED';
    err.payload = payload402;
    throw err;
  }

  // Parse JSON défensif
  let payload = null;
  try { payload = await res.json(); } catch { /* HTML ou body vide */ }

  // 403 + { error: 'plan_upgrade_required' } = fonctionnalité non incluse dans
  // le plan (middleware requireCapability, chantier « palier Découverte »).
  // On NE redirige PAS ici, contrairement au 402 : l'UI verrouille déjà la zone
  // via <LockedFeature> et affiche un CTA vers /abonnement. Ce chemin ne sert
  // que de filet — il enrichit l'erreur pour que l'appelant puisse afficher la
  // bonne modale au lieu d'un bandeau d'erreur générique.
  if (res.status === 403 && payload?.error === 'plan_upgrade_required') {
    const err = new Error("Cette fonctionnalité n'est pas incluse dans votre formule.");
    err.status = 403;
    err.code = 'PLAN_UPGRADE_REQUIRED';
    err.capability = payload.capability || null;
    err.currentPlan = payload.current_plan || null;
    err.payload = payload;
    throw err;
  }

  if (!res.ok) {
    const err = new Error(payload?.error || payload?.message || `Erreur ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

export const apiGet    = (path)         => apiFetch(path, { method: 'GET' });
export const apiPost   = (path, body)   => apiFetch(path, { method: 'POST',   body: body ? JSON.stringify(body) : undefined });
export const apiPut    = (path, body)   => apiFetch(path, { method: 'PUT',    body: body ? JSON.stringify(body) : undefined });
export const apiPatch  = (path, body)   => apiFetch(path, { method: 'PATCH',  body: body ? JSON.stringify(body) : undefined });
export const apiDelete = (path)         => apiFetch(path, { method: 'DELETE' });
